
import React, { useState } from "react"
import { Bell } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Badge } from "@/components/ui/badge"
import { formatDistanceToNow } from "date-fns"
import { ptBR } from "date-fns/locale"
import { useNavigate } from "react-router-dom"
import { useNotifications, useUnreadNotificationsCount, useMarkNotificationAsRead } from "@/hooks/useNotifications"

const NotificationBell = () => {
  const { data: notificationsData = [], isLoading, error } = useNotifications()
  const unreadCount = useUnreadNotificationsCount()
  const markAsRead = useMarkNotificationAsRead()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  
  // Log de erro se houver problema ao carregar notificações
  if (error) {
    console.error("❌ Erro ao carregar notificações no sino:", error)
  }
  
  const notificationList = notificationsData

  const handleNotificationClick = (notification: any) => {
    // Marcar como lida ANTES de navegar
    if (!notification.is_read) {
      markAsRead.mutate(notification.id)
    }
    
    // Navegar baseado no tipo de notificação
    if (notification.type === 'booking') {
      navigate('/areas-comuns')
    } else if (notification.type === 'incident') {
      navigate('/ocorrencias')
    } else if (notification.type === 'announcement') {
      navigate('/avisos')
    } else if (notification.type === 'charge') {
      navigate('/financeiro')
    } else if (notification.type === 'system' && notification.data?.access_request_id) {
      // Para solicitações de acesso
      navigate('/agenda')
    } else if (notification.type === 'message') {
      // Para mensagens, navegar para a conversa específica
      if (notification.data?.conversation_id) {
        navigate(`/mensagens?conversation=${notification.data.conversation_id}`)
      } else {
        navigate('/mensagens')
      }
    } else {
      // Default para notificações de sistema
      navigate('/agenda')
    }
    
    setOpen(false)
  }

  const getNotificationIcon = (type: string, data?: any) => {
    switch (type) {
      case 'booking':
        return '📅'
      case 'announcement':
        return '📢'
      case 'incident':
        return '⚠️'
      case 'message':
        return '💬'
      case 'charge':
        return '💰'
      case 'system':
        // Para access_requests, usar ícones específicos
        if (data?.access_request_id) {
          if (data?.status === 'approved') return '✅'
          if (data?.status === 'rejected') return '❌'
          if (data?.status === 'pending') return '🚪'
        }
        return '🔔'
      default:
        return '🔔'
    }
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notificações</span>
          {unreadCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {unreadCount} não lidas
            </Badge>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {notificationList.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            Nenhuma notificação
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {notificationList.slice(0, 10).map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={`flex flex-col items-start p-3 cursor-pointer transition-all duration-200 ${
                  notification.is_read 
                    ? 'opacity-60 hover:bg-gray-25' 
                    : 'hover:bg-gray-50 animate-fade-in'
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start gap-2 w-full">
                  <span className="text-lg">{getNotificationIcon(notification.type, notification.data)}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className={`font-medium text-sm truncate ${
                        notification.is_read ? 'text-gray-500' : 'text-gray-900'
                      }`}>
                        {notification.title}
                      </p>
                      {!notification.is_read && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full ml-2 flex-shrink-0 animate-pulse" />
                      )}
                    </div>
                    <p className={`text-xs mt-1 line-clamp-2 ${
                      notification.is_read ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {notification.message}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatDistanceToNow(new Date(notification.created_at), { 
                        addSuffix: true, 
                        locale: ptBR 
                      })}
                    </p>
                  </div>
                </div>
              </DropdownMenuItem>
            ))}
          </div>
        )}
        
        {notificationList.length > 10 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="text-center text-sm text-blue-600 cursor-pointer"
              onClick={() => {
                navigate('/notifications')
                setOpen(false)
              }}
            >
              Ver todas as notificações
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default NotificationBell
